import {
  DEFAULT_VISUAL_FPS,
  MAX_VISUAL_FPS,
  MIN_VISUAL_FPS,
  visualIntervalMsToFps,
  type SimulationDurationMode,
  type SimulationRunMode,
} from '../lib/simulationDuration';

// ------------------------------------------------------------
// Per-user / per-project simulation run preferences
// ------------------------------------------------------------

export interface ProjectRunConfig {
  run_mode: SimulationRunMode;
  duration_mode: SimulationDurationMode;
  duration_minutes: number;
  visual_fps: number;
  show_products: boolean;
  active_scenario_id?: string | null;
}

export interface SimulationRunPreferencesV1 {
  version: 1;
  global_defaults: ProjectRunConfig;
  by_project: Record<string, ProjectRunConfig>;
}

const LOCAL_KEY = 'sim-designer-run-preferences-v1';

export function defaultProjectRunConfig(): ProjectRunConfig {
  return {
    run_mode: 'realtime',
    duration_mode: 'until_stopped',
    duration_minutes: 60,
    visual_fps: DEFAULT_VISUAL_FPS,
    show_products: true,
    active_scenario_id: null,
  };
}

export function createDefaultRunPreferences(): SimulationRunPreferencesV1 {
  return {
    version: 1,
    global_defaults: defaultProjectRunConfig(),
    by_project: {},
  };
}

function clampRunConfig(config: ProjectRunConfig): ProjectRunConfig {
  return {
    ...config,
    duration_minutes: Math.min(10_080, Math.max(1, Math.round(config.duration_minutes))),
    visual_fps: Math.min(
      MAX_VISUAL_FPS,
      Math.max(MIN_VISUAL_FPS, Math.round(config.visual_fps)),
    ),
    show_products: config.show_products !== false,
    active_scenario_id: config.active_scenario_id ?? null,
  };
}

function migrateLegacyVisualFps(raw: Record<string, unknown>): number | undefined {
  if (typeof raw.visual_fps === 'number') {
    return raw.visual_fps;
  }
  if (typeof raw.visual_refresh_ms === 'number') {
    return visualIntervalMsToFps(raw.visual_refresh_ms);
  }
  return undefined;
}

export function parseProjectRunConfig(raw: unknown): ProjectRunConfig | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as Record<string, unknown>;
  const runMode = value.run_mode === 'calculate' ? 'calculate' : 'realtime';
  const durationMode =
    value.duration_mode === 'fixed' ? 'fixed' : 'until_stopped';
  const durationMinutes =
    typeof value.duration_minutes === 'number' ? value.duration_minutes : 60;
  const visualFps = migrateLegacyVisualFps(value) ?? DEFAULT_VISUAL_FPS;
  const showProducts = value.show_products !== false;
  const activeScenarioId =
    typeof value.active_scenario_id === 'string'
      ? value.active_scenario_id
      : value.active_scenario_id === null
        ? null
        : undefined;

  return clampRunConfig({
    run_mode: runMode,
    duration_mode: durationMode,
    duration_minutes: durationMinutes,
    visual_fps: visualFps,
    show_products: showProducts,
    active_scenario_id: activeScenarioId,
  });
}

export function parseSimulationRunPreferences(
  raw: unknown,
): SimulationRunPreferencesV1 | null {
  if (!raw || typeof raw !== 'object') return null;
  const value = raw as Partial<SimulationRunPreferencesV1>;
  if (value.version !== 1) return null;

  const globalDefaults =
    parseProjectRunConfig(value.global_defaults) ?? defaultProjectRunConfig();
  const byProject: Record<string, ProjectRunConfig> = {};
  if (value.by_project && typeof value.by_project === 'object') {
    for (const [projectId, config] of Object.entries(value.by_project)) {
      const parsed = parseProjectRunConfig(config);
      if (parsed) byProject[projectId] = parsed;
    }
  }

  return {
    version: 1,
    global_defaults: globalDefaults,
    by_project: byProject,
  };
}

export function resolveProjectRunConfig(
  preferences: SimulationRunPreferencesV1,
  projectId: string,
): ProjectRunConfig {
  return clampRunConfig(
    preferences.by_project[projectId] ?? preferences.global_defaults,
  );
}

export function loadLocalRunPreferences(): SimulationRunPreferencesV1 {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    if (!raw) return createDefaultRunPreferences();
    return parseSimulationRunPreferences(JSON.parse(raw)) ?? createDefaultRunPreferences();
  } catch {
    return createDefaultRunPreferences();
  }
}

export function saveLocalRunPreferences(preferences: SimulationRunPreferencesV1) {
  localStorage.setItem(LOCAL_KEY, JSON.stringify(preferences));
}

export function mergeProjectRunConfig(
  preferences: SimulationRunPreferencesV1,
  projectId: string,
  config: ProjectRunConfig,
): SimulationRunPreferencesV1 {
  return {
    ...preferences,
    by_project: {
      ...preferences.by_project,
      [projectId]: clampRunConfig(config),
    },
  };
}
